/**
 * @file: usePredefinedConstructionItems.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { useCachedQuery } from '@euler/functions/useCachedQuery';
import { ConstructionProcedure } from '@euler/model';
import { PredefinedConstructionWorkItemsResponse } from '@euler/services';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorReturn } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { getPyInitials } from '@euler/utils/pinyin/getPyInitials';
import produce from 'immer';
import { nanoid } from 'nanoid/non-secure';

export const kPredefinedWorkItemsVersion = '1.0';
export const kCustomConstructionWorkItemCategoryId = '__CUSTOM__';
export class CustomWorkItemAlreadyExistsError extends Error {
  constructor(name: string) {
    super(`Custom work item ${name} already exists`);
  }
}

export class CustomWorkItemArgumentError extends Error {
  constructor(readonly argumentName: 'name' | 'procedures') {
    super(`Custom work item ${argumentName} is required`);
  }
}

export function usePredefinedConstructionItems() {
  const { inventoryService } = useServiceFactory();
  const user = useCurrentUserDangerously();
  const path = `store/${user.store.id}/construction-items.json`;

  const cachedQuery = useCachedQuery<PredefinedConstructionWorkItemsResponse>(
    path,
    4 * 3600,
    async cached => {
      const res = await inventoryService
        .getPredefinedConstructionWorkItems()
        .catch(
          onErrorReturn<PredefinedConstructionWorkItemsResponse>({
            version: kPredefinedWorkItemsVersion,
            categories: [],
          }),
        );
      const customCategory = cached?.categories.find(
        x => x.id === kCustomConstructionWorkItemCategoryId,
      );
      if (customCategory != null) {
        res.categories.push(customCategory);
      }
      return res;
    },
    true,
    { validate: object => object.version === kPredefinedWorkItemsVersion },
  );

  const addCustomWorkItem = usePersistFn(
    (name: string, procedures: ConstructionProcedure[]) => {
      if (cachedQuery.state == null) {
        return;
      }

      name = name.trim();
      if (!name) {
        throw new CustomWorkItemArgumentError('name');
      }

      if (!procedures.length) {
        throw new CustomWorkItemArgumentError('procedures');
      }

      for (const category of cachedQuery.state.categories) {
        for (const group of category.groups) {
          for (const item of group.items) {
            if (item.name === name) {
              throw new CustomWorkItemAlreadyExistsError(name);
            }
          }
        }
      }

      const object = produce(cachedQuery.state, draft => {
        let category = draft.categories.find(
          x => x.id === kCustomConstructionWorkItemCategoryId,
        );
        if (category == null) {
          category = {
            id: kCustomConstructionWorkItemCategoryId,
            name: '自定义',
            groups: [
              {
                id: '',
                name: '我的自定义施工项目',
                items: [],
              },
            ],
          };
          draft.categories.push(category);
        }

        category.groups[0].items.unshift({
          id: nanoid(),
          name,
          pyInitial: getPyInitials(name),
          template: {
            id: nanoid(),
            procedures,
          },
        });
      });

      cachedQuery.update(object);
    },
  );

  const removeCustomWorkItem = usePersistFn((name: string) => {
    if (!cachedQuery.state) return;
    const categoryIndex = cachedQuery.state.categories.findIndex(
      x => x.id === kCustomConstructionWorkItemCategoryId,
    );
    if (categoryIndex < 0) return;
    const index = cachedQuery.state.categories[
      categoryIndex
    ].groups[0].items.findIndex(x => x.name === name);
    if (index >= 0) {
      const updated = produce(cachedQuery.state, draft => {
        draft.categories[categoryIndex].groups[0].items.splice(index, 1);
      });
      cachedQuery.update(updated);
    }
  });

  const clearCustomWorkItems = usePersistFn(() => {
    if (!cachedQuery.state) return;
    const categoryIndex = cachedQuery.state.categories.findIndex(
      x => x.id === kCustomConstructionWorkItemCategoryId,
    );
    if (categoryIndex < 0) return;
    const updated = produce(cachedQuery.state, draft => {
      draft.categories[categoryIndex].groups[0].items = [];
    });
    cachedQuery.update(updated);
  });

  return {
    ...cachedQuery,
    addCustomWorkItem,
    removeCustomWorkItem,
    clearCustomWorkItems,
  };
}
